import { useEffect, useState } from 'react';
import type { ReactNode } from 'react';
import { ChevronLeft, ChevronRight, X } from 'lucide-react';
import { PhoneFrame } from './PhoneFrame';
import { AppScreen, StatusBar } from './kit';
import { prefersReducedMotion, useInView } from './useInView';
import './ReceiptScanMotionDemo.css';

const STEP = {
  banner: 1,
  receipt: 2,
  name: 3,
  amount: 4,
  date: 5,
  repeat: 6,
  category: 7,
  bannerOut: 8,
  done: 9,
} as const;

const FINAL = STEP.done;

const REPEATS = [
  'Does not repeat',
  'Weekly',
  'Every 2 weeks',
  'Monthly',
  'Yearly',
] as const;

interface TextFieldProps {
  label: string;
  value?: string;
  filled?: boolean;
  placeholder?: string;
}

function TextField({
  label,
  value,
  filled = false,
  placeholder,
}: TextFieldProps) {
  return (
    <div className='rs-field'>
      <span className='rs-label'>{label}</span>
      <div className='rs-input'>
        {filled ? (
          <>
            <span className='rs-wash' aria-hidden='true' />
            <span className='rs-value'>{value}</span>
            <span className='rs-underline' aria-hidden='true' />
          </>
        ) : placeholder ? (
          <span className='rs-placeholder'>{placeholder}</span>
        ) : null}
      </div>
    </div>
  );
}

interface RowFieldProps {
  label: string;
  filled: boolean;
  children: ReactNode;
}

function RowField({ label, filled, children }: RowFieldProps) {
  return (
    <div className='rs-field'>
      <span className='rs-label'>{label}</span>
      <div className='rs-row'>
        {filled ? (
          <>
            <span className='rs-wash' aria-hidden='true' />
            <span className='rs-value'>{children}</span>
            <span className='rs-underline' aria-hidden='true' />
          </>
        ) : null}
        <ChevronRight className='rs-chevron' size={15} strokeWidth={2.2} />
      </div>
    </div>
  );
}

function ReceiptThumb({ visible }: { visible: boolean }) {
  const className = ['rs-thumb', visible ? 'is-visible' : '']
    .filter(Boolean)
    .join(' ');

  return (
    <div className={className} aria-hidden={!visible}>
      <span className='rs-thumb__hand' aria-hidden='true' />
      <span className='rs-thumb__thumb' aria-hidden='true' />
      <span className='rs-paper' aria-hidden='true'>
        <span className='rs-paper__store'>Riverside Art Co</span>
        <span className='rs-paper__addr'>Imagine. Create. Play.</span>
        <span className='rs-paper__addr'>1234 Riverfront Way</span>
        <span className='rs-paper__addr'>Riverside, ST 54321</span>
        <span className='rs-paper__rule' />
        <span className='rs-paper__meta'>
          <span>Date: 01/25/26</span>
          <span>Time: 2:15 PM</span>
        </span>
        <span className='rs-paper__meta'>
          <span>Receipt #: 182568</span>
          <span>Cashier: 7</span>
        </span>
        <span className='rs-paper__rule' />
        <span className='rs-paper__line rs-paper__line--82' />
        <span className='rs-paper__line rs-paper__line--64' />
        <span className='rs-paper__line rs-paper__line--74' />
        <span className='rs-paper__line rs-paper__line--52' />
        <span className='rs-paper__rule' />
        <span className='rs-paper__total' />
        <span className='rs-paper__barcode' />
        <span className='rs-paper__digits'>0 182568 15668 4</span>
      </span>
      {visible ? (
        <>
          <span className='rs-shutter' aria-hidden='true' />
          <span className='rs-sweep' aria-hidden='true' />
        </>
      ) : null}
      <span className='rs-thumb__close' aria-hidden='true'>
        <X size={10} strokeWidth={3} />
      </span>
    </div>
  );
}

export function ReceiptScanMotionDemo() {
  const { ref, seen } = useInView<HTMLDivElement>({ threshold: 0.25 });
  const reduced = prefersReducedMotion();
  const [stage, setStage] = useState<number>(() =>
    prefersReducedMotion() ? FINAL : 0
  );

  useEffect(() => {
    if (reduced) {
      setStage(FINAL);
      return;
    }

    if (!seen) return;

    const timers = [
      window.setTimeout(() => setStage(STEP.banner), 140),
      window.setTimeout(() => setStage(STEP.receipt), 780),
      window.setTimeout(() => setStage(STEP.name), 1560),
      window.setTimeout(() => setStage(STEP.amount), 2080),
      window.setTimeout(() => setStage(STEP.date), 2600),
      window.setTimeout(() => setStage(STEP.repeat), 3120),
      window.setTimeout(() => setStage(STEP.category), 3640),
      window.setTimeout(() => setStage(STEP.bannerOut), 4140),
      window.setTimeout(() => setStage(STEP.done), 4900),
    ];

    return () => {
      timers.forEach((timer) => window.clearTimeout(timer));
    };
  }, [seen, reduced]);

  const nameFilled = stage >= STEP.name;
  const amountFilled = stage >= STEP.amount;
  const dateFilled = stage >= STEP.date;
  const repeatFilled = stage >= STEP.repeat;
  const categoryFilled = stage >= STEP.category;

  const bannerClass = [
    'rs-banner',
    stage >= STEP.bannerOut ? 'is-out' : stage >= STEP.banner ? 'is-in' : '',
  ]
    .filter(Boolean)
    .join(' ');

  return (
    <div
      ref={ref}
      className={['rs-demo', reduced ? 'is-reduced' : '']
        .filter(Boolean)
        .join(' ')}
    >
      <PhoneFrame size='md'>
        <AppScreen size='md'>
          <div className='rs-screen'>
            <StatusBar />

            <header className='rs-header'>
              <span className='rs-back' aria-hidden='true'>
                <ChevronLeft size={19} strokeWidth={2.6} />
              </span>
              <h1 className='rs-title'>New Expense</h1>
            </header>

            <p className='rs-subtitle'>Record an expense for your household.</p>

            <div
              className={bannerClass}
              aria-live='polite'
              aria-hidden={!(stage >= STEP.banner && stage < STEP.bannerOut)}
            >
              <div className='rs-banner__inner'>
                <span className='rs-spinner' aria-hidden='true' />
                <span className='rs-banner__text'>Scanning receipt...</span>
              </div>
            </div>

            <main className='rs-body'>
              <section className='rs-card'>
                <TextField
                  label='Name'
                  value='Riverside Art Co - art supplies'
                  filled={nameFilled}
                />
                <TextField
                  label='Amount'
                  value='156.68'
                  filled={amountFilled}
                />
                <RowField label='Date' filled={dateFilled}>
                  January 25, 2026
                </RowField>

                <div className='rs-field'>
                  <span className='rs-label'>Repeat</span>
                  <div className='rs-chips'>
                    {REPEATS.map((option, index) => {
                      const active = index === 0 && repeatFilled;
                      return (
                        <span
                          key={option}
                          className={[
                            'rs-chip',
                            index === 0 ? 'rs-chip--primary' : '',
                            active ? 'is-on' : '',
                          ]
                            .filter(Boolean)
                            .join(' ')}
                        >
                          {option}
                        </span>
                      );
                    })}
                  </div>
                </div>

                <RowField label='Category' filled={categoryFilled}>
                  <span className='rs-cat'>
                    <span className='rs-cat__dot' aria-hidden='true' />
                    Other
                  </span>
                </RowField>

                <TextField
                  label='Note (optional)'
                  placeholder='Any details...'
                />
              </section>

              <section className='rs-attach'>
                <p className='rs-attach__label'>Attachments (optional)</p>
                <div className='rs-attach__row'>
                  <ReceiptThumb visible={stage >= STEP.receipt} />
                  <span className='rs-add'>+ Add Photo</span>
                  <span className='rs-add'>+ Add PDF</span>
                </div>
              </section>
            </main>
          </div>
        </AppScreen>
      </PhoneFrame>
    </div>
  );
}
